import type { ChatAttachment, ChatMessage } from '../models/chat.model'
import { documentType } from './document-type'

/**
 * Save a conversation as a standalone .html file.
 *
 * Self-contained by design: the styles are inlined and nothing is fetched, so
 * the saved file opens years later on a machine that has never heard of this
 * app. The one thing it cannot carry is the attachments themselves — those live
 * behind the backend's auth — so each is written as its name, size and a link
 * rather than silently dropped.
 *
 * Everything that came from a person is escaped. A saved chat is a document
 * someone will open in a browser, and a message body is the last place to trust
 * markup.
 */

const esc = (s: string): string =>
  s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string,
  )

const fmtTime = (ms: number): string =>
  new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

const fmtDay = (ms: number): string =>
  new Date(ms).toLocaleDateString([], { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })

const fmtSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return ''
  const kb = bytes / 1024
  return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(1)} MB`
}

function attachmentHtml(a: ChatAttachment): string {
  const name = esc(a.name)
  const url = esc(a.url)
  const size = esc(fmtSize(a.size))

  // One case per branch of ChatAttachmentBubble, same shapes and same strips.
  if (a.kind === 'image') {
    return `<a class="att image" href="${url}"><img src="${url}" alt="${name}" loading="lazy" /><div class="cap">${name}</div></a>`
  }
  if (a.kind === 'video') {
    return `<div class="att video"><video src="${url}" controls preload="metadata"></video><div class="cap"><span>${name}</span><span class="size">${size}</span></div></div>`
  }
  if (a.kind === 'audio') {
    return `<div class="att audio"><div class="head"><span>${name}</span><span class="size">${size}</span></div><audio src="${url}" controls preload="metadata"></audio></div>`
  }
  const { ext, color } = documentType(a.name)
  const details = [size, esc(ext)].filter(Boolean).join(' · ')
  return `<a class="att doc" href="${url}"><span class="tile" style="background:${color}">${esc(ext)}</span><span class="meta"><span class="name">${name}</span><span class="size">${details}</span></span></a>`
}

function messageHtml(m: ChatMessage, localUserId: string): string {
  const mine = m.senderId === localUserId
  // A bubble OR an attachment, never both — the panel swaps one for the other.
  const content = m.attachment
    ? attachmentHtml(m.attachment)
    : `<div class="bubble">${esc(m.text).replace(/\n/g, '<br />')}</div>`
  return `<div class="row ${mine ? 'mine' : ''}">
      ${content}
      <div class="time">${esc(fmtTime(m.timestamp))}</div>
    </div>`
}

/** The document itself — layout mirrors the panel: mine on the right, theirs on
 *  the left, grouped under the day they were sent. */
export function buildChatHtml(opts: {
  title: string
  messages: ChatMessage[]
  localUserId: string
  savedAt?: number
}): string {
  const { title, messages, localUserId, savedAt = Date.now() } = opts
  const ordered = [...messages].sort((a, b) => a.timestamp - b.timestamp)

  const body: string[] = []
  let day = ''
  for (const m of ordered) {
    const d = fmtDay(m.timestamp)
    if (d !== day) {
      day = d
      body.push(`<div class="day">${esc(d)}</div>`)
    }
    body.push(messageHtml(m, localUserId))
  }
  if (ordered.length === 0) body.push('<div class="empty">No messages in this conversation.</div>')

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${esc(title)}</title>
<style>
  /* The panel's own geometry, written out.
   *
   * Every value here is the literal the chat UI resolves to — Tailwind's scale
   * and the app's tokens — rather than a fresh design: bubbles rounded-2xl with
   * the sending corner squared off (rounded-br-sm / rounded-bl-sm), px-3 py-2,
   * text-sm at leading-relaxed, gap-0.5 to the 10px timestamp, capped at 85%.
   * Tailwind itself cannot come along: a saved file has to stand on its own with
   * no stylesheet to fetch. */
  :root {
    color-scheme: light dark;
    --primary: #0166B4;          /* bg-primary */
    --panel: #f7f7f7;            /* --panelcolor */
    --panel-text: #0f172a;       /* --vc-panel-text */
    --bubble: #f1f5f9;           /* bg-slate-100 */
    --bubble-text: #1e293b;      /* text-slate-800 */
    --time: #94a3b8;             /* text-slate-400 */
    --meta: #64748b;             /* text-slate-500 */
  }
  @media (prefers-color-scheme: dark) {
    :root {
      --panel: #272727; --panel-text: #e2e8f0;
      --bubble: #262626;         /* dark:bg-muted */
      --bubble-text: #fafafa;    /* dark:text-foreground */
      --time: #a1a1aa; --meta: #a1a1aa;
    }
  }
  * { box-sizing: border-box; }
  body {
    margin: 0; background: var(--panel); color: var(--panel-text);
    font: 14px/1.5 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  }
  /* px-4 py-3 space-y-3, on the panel's own width */
  .thread { max-width: 420px; margin: 0 auto; padding: 12px 16px 32px;
            display: flex; flex-direction: column; gap: 12px; }
  header { padding: 12px 0 4px; }
  h1 { font-size: 15px; font-weight: 600; margin: 0; }
  .sub { font-size: 10px; color: var(--time); margin-top: 2px; }
  .day { align-self: center; font-size: 10px; color: var(--time); padding: 2px 0; }

  /* flex flex-col gap-0.5, items-end when it is mine */
  .row { display: flex; flex-direction: column; gap: 2px; align-items: flex-start; }
  .row.mine { align-items: flex-end; }

  /* max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed */
  .bubble {
    max-width: 85%; border-radius: 16px; padding: 8px 12px;
    font-size: 14px; line-height: 1.625;
    background: var(--bubble); color: var(--bubble-text);
    border-bottom-left-radius: 2px;                 /* rounded-bl-sm */
    white-space: pre-wrap; overflow-wrap: anywhere;
  }
  .mine .bubble {
    background: var(--primary); color: #fff;
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 2px;                /* rounded-br-sm */
  }

  /* text-[10px] text-slate-400 */
  .time { font-size: 10px; color: var(--time); }

  /* ChatAttachmentBubble — image: rounded-2xl, max-w-[260px], caption strip */
  .att { border-radius: 16px; overflow: hidden; border-bottom-left-radius: 2px; }
  .mine .att { border-bottom-left-radius: 16px; border-bottom-right-radius: 2px; }
  .att.image { display: block; max-width: 260px; text-decoration: none; }
  .att.image img { width: 100%; height: auto; max-height: 240px; object-fit: cover; display: block; }
  .att.image .cap { background: var(--bubble); padding: 6px 12px; font-size: 10px; color: var(--meta);
                    white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  /* video: max-w-[280px] with a name/size strip */
  .att.video { max-width: 280px; background: var(--bubble); }
  .att.video video { width: 100%; height: auto; max-height: 260px; background: #000; display: block; }
  .att.video .cap { display: flex; align-items: center; gap: 6px; padding: 6px 12px;
                    font-size: 10px; color: var(--meta); }
  .att.video .cap .size { margin-left: auto; }
  /* audio: px-3 py-2, max-w-[280px] */
  .att.audio { max-width: 280px; width: 100%; background: var(--bubble); padding: 8px 12px; }
  .att.audio .head { display: flex; align-items: center; gap: 8px; font-size: 12px;
                     color: var(--bubble-text); margin-bottom: 6px; }
  .att.audio .head .size { font-size: 10px; color: var(--time); margin-left: auto; }
  .att.audio audio { width: 100%; height: 32px; }
  /* document: flex gap-3, px-3 py-2.5, 36px tile in the type's colour */
  .att.doc { display: flex; align-items: center; gap: 12px; max-width: 280px;
             background: var(--bubble); padding: 10px 12px; text-decoration: none; color: inherit; }
  .att.doc .tile { width: 36px; height: 36px; border-radius: 8px; flex: none; color: #fff;
                   display: flex; align-items: center; justify-content: center;
                   font-size: 9px; font-weight: 700; }
  .att.doc .meta { flex: 1; min-width: 0; }
  .att.doc .name { display: block; font-size: 12px; font-weight: 500; color: var(--bubble-text);
                   white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .att.doc .size { display: block; font-size: 10px; color: var(--time); }
  .empty { color: var(--time); text-align: center; padding: 32px 0; font-size: 13px; }
</style>
</head>
<body>
  <div class="thread">
    <header>
      <h1>${esc(title)}</h1>
      <div class="sub">${esc(`${ordered.length} message${ordered.length === 1 ? '' : 's'} · saved ${new Date(savedAt).toLocaleString()}`)}</div>
    </header>
    ${body.join('\n    ')}
  </div>
</body>
</html>`
}

/** Filesystem-safe, and recognisable a year later. */
function fileName(title: string, savedAt: number): string {
  const slug = title.trim().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').slice(0, 60) || 'chat'
  const d = new Date(savedAt)
  const stamp = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  return `chat-${slug}-${stamp}.html`
}

/**
 * Build the document and hand it to the browser to save. No prompt, no format
 * to choose — saving a chat produces the file.
 */
export function saveChatAsHtml(opts: {
  title: string
  messages: ChatMessage[]
  localUserId: string
}): void {
  const savedAt = Date.now()
  const html = buildChatHtml({ ...opts, savedAt })
  const url = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }))
  try {
    const a = document.createElement('a')
    a.href = url
    a.download = fileName(opts.title, savedAt)
    document.body.appendChild(a)
    a.click()
    a.remove()
  } finally {
    // The save has already started; the blob can go.
    URL.revokeObjectURL(url)
  }
}
